import { Box, Grid, Typography } from "@material-ui/core";
import UnitedLogo from "../../assets/logo.png";
import { formatDate, formatTime } from "../../utils/date-helper";
import { RecordViewProps } from "../../interface/RecordViewProps";
import {
  HaulageRecordFields,
  SoilQuantity,
} from "../../interface/HaulageRecordFields";

const RecordView = ({ fields, styles }: RecordViewProps) => {
  let marginBottom = 12;
  let showLogo = true;
  let fontSize = 16;

  if (styles) {
    marginBottom = styles.marginBottom;
    showLogo = styles.showLogo;
    fontSize = styles.fontSize;
  }

  // A row renders when its key is present on `fields`; a null value renders the label
  // with a blank value, matching how the Main Pit record has always behaved.
  //
  // Takes the KEY rather than a value plus a separate presence flag: every field on
  // HaulageRecordFields is optional, so a value/flag pair could disagree without the
  // compiler noticing — and a mismatched pair silently drops a row from a legal record.
  const row = (
    label: string,
    key: keyof HaulageRecordFields,
    render?: (value: any) => React.ReactNode
  ) =>
    key in fields ? (
      <Typography style={{ marginBottom, fontSize }}>
        <b>{label} </b>
        {render ? render(fields[key]) : (fields[key] as any)}
      </Typography>
    ) : null;

  const renderSoilQuantity = (quantity: SoilQuantity | null | undefined) => {
    if (!quantity) return null;
    if (quantity.kind === "estimated") return quantity.text;
    return (
      <>
        <div>
          <i>Gross:</i> {quantity.grossKg} KG
        </div>
        <div>
          <i>Net:</i> {quantity.netKg} KG
        </div>
        <div>
          <i>Tare:</i> {quantity.tareKg} KG
        </div>
      </>
    );
  };

  return (
    <Box
      sx={{
        border: "2px solid black",
        padding: "20px",
        marginTop: "20px",
        marginBottom,
      }}
      className="ticket-render"
    >
      <Box
        style={{
          marginBottom,
        }}
      >
        <Grid container>
          <Grid item xs>
            <Typography variant="h5" style={{ marginBottom }}>
              <b>United</b> Soils Management Ltd.
            </Typography>
          </Grid>
          {showLogo && (
            <Grid item xs={4}>
              <img
                src={UnitedLogo}
                style={{ maxWidth: 125, paddingTop: "7px" }}
                alt="United Logo"
              />
            </Grid>
          )}
        </Grid>
      </Box>
      <Grid container>
        <Grid item xs>
          <Typography style={{ marginBottom }}>Haulage Record</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography style={{ marginBottom, fontSize }}>
            <b>#{fields.ticketNumber}</b>
          </Typography>
        </Grid>
      </Grid>
      <Box sx={{ marginBottom }}>
        <Typography variant="h6" style={{ marginBottom }}>
          LOADING SITE
        </Typography>
        {row("Project Address:", "projectAddress")}
        {row("Soil Quality:", "soilQuality")}
        {"saltImpactedNote" in fields && (
          <Typography
            style={{ marginBottom, textDecoration: "underline", fontSize }}
          >
            {fields.saltImpactedNote}
          </Typography>
        )}
        {row("Contractor:", "contractor")}
        {row("Contact Name:", "contactName")}
        {row("Number:", "contactNumber")}
        {row("Email:", "contactEmail")}
        {/* Material has always been conditional on having a value, not merely present. */}
        {fields.material && row("Material:", "material")}
        {row("Soil Quantity:", "soilQuantity", renderSoilQuantity)}
        {row("Date Loaded:", "timeLoaded", (v) =>
          v ? formatDate(v, !!fields.isSoilFLOLoad) : null
        )}
        {row("Time Loaded:", "timeLoaded", (v) =>
          v ? formatTime(v, !!fields.isSoilFLOLoad) : null
        )}
        <Typography style={{ marginBottom, fontSize }}>
          {`Entry of Time Loaded is Loading Site confirmation that the soil was loaded under supervision, is approved for disposal at United Soils and complies with transport regulations.`}
        </Typography>
        {row("Hauling Company:", "haulingCompany")}
        {row("Driver Name:", "driverName")}
        {row("Licence Plate Number:", "licencePlate")}
        {row("Truck No.:", "truckNumber")}
        <Typography variant="h6" style={{ marginBottom }}>
          RECEIVING SITE
        </Typography>
        <Typography style={{ marginBottom, fontSize }}>
          <b>Location: </b>
          {`United Soils Management 14245 Ninth Line, Stouffville, ON`}
        </Typography>
        {row("Receiving Site Contact:", "receivingContactName")}
        {row("Receiving Site Number:", "receivingContactNumber")}
        {row("Receiving Site Email:", "receivingContactEmail")}
        {/* Leading space preserves the original spacing after the bold label. */}
        {row("Receiving Date:", "timeReceived", (v) => (
          <>{" "}{v && formatDate(v, false)}</>
        ))}
        {row("Receiving Time:", "timeReceived", (v) => (
          <>{" "}{v && formatTime(v, false)}</>
        ))}
        <Typography style={{ marginBottom, fontSize }}>
          {`Entry of Receiving Time is acknowledgement that soil was received and placed at United Soils.`}
        </Typography>
      </Box>
    </Box>
  );
};

export default RecordView;
